import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import dayjs from 'dayjs';

interface DatePickerProps {
  value?: string;
  onChange?: (date: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  showTimeSelect?: boolean;
  minDate?: string | Date;
  maxDate?: string | Date;
  allowedDates?: string[];
  invalidDateMessage?: string;
  error?: boolean;
  helperText?: string;
  inputProps?: Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'value' | 'onChange' | 'min' | 'max'
  >;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value = '',
  onChange,
  className = '',
  disabled = false,
  placeholder,
  showTimeSelect = false,
  minDate,
  maxDate,
  allowedDates,
  invalidDateMessage = 'Selected date is not available.',
  error = false,
  helperText,
  inputProps = {},
}) => {
  const inputType = showTimeSelect ? 'datetime-local' : 'date';
  const containerRef = useRef<HTMLDivElement>(null);

  const formatForInput = useCallback(
    (val: string) => {
      if (!val) return '';
      const parsed = dayjs(val);
      if (!parsed.isValid()) return '';
      return showTimeSelect
        ? parsed.format('YYYY-MM-DDTHH:mm')
        : parsed.format('YYYY-MM-DD');
    },
    [showTimeSelect]
  );

  const [internalValue, setInternalValue] = useState(() => formatForInput(value));
  const [hasInvalidSelection, setHasInvalidSelection] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const useCustomCalendar = !showTimeSelect && Boolean(allowedDates && allowedDates.length > 0);

  const normalizedAllowedDates = useMemo(() => {
    if (!allowedDates || allowedDates.length === 0) return null;
    return new Set(allowedDates.map((date) => formatForInput(date)).filter(Boolean));
  }, [allowedDates, formatForInput]);

  const minFormatted = useMemo(() => {
    if (!minDate) return undefined;
    try {
      const parsed = dayjs(minDate);
      return parsed.isValid()
        ? parsed.format(showTimeSelect ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD')
        : undefined;
    } catch { return undefined; }
  }, [minDate, showTimeSelect]);

  const maxFormatted = useMemo(() => {
    if (!maxDate) return undefined;
    try {
      const parsed = dayjs(maxDate);
      return parsed.isValid()
        ? parsed.format(showTimeSelect ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD')
        : undefined;
    } catch { return undefined; }
  }, [maxDate, showTimeSelect]);

  const initialCalendarMonth = useMemo(() => {
    const selectedDate = formatForInput(value);
    if (selectedDate) return dayjs(selectedDate).startOf('month');
    const firstAllowedDate = normalizedAllowedDates ? Array.from(normalizedAllowedDates).sort()[0] : null;
    if (firstAllowedDate) return dayjs(firstAllowedDate).startOf('month');
    if (minFormatted) return dayjs(minFormatted).startOf('month');
    return dayjs().startOf('month');
  }, [formatForInput, minFormatted, normalizedAllowedDates, value]);

  const [currentMonth, setCurrentMonth] = useState(initialCalendarMonth);

  useEffect(() => {
    setInternalValue(formatForInput(value));
    setHasInvalidSelection(false);
  }, [value, formatForInput]);

  useEffect(() => { setCurrentMonth(initialCalendarMonth); }, [initialCalendarMonth]);

  useEffect(() => {
    if (!useCustomCalendar) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [useCustomCalendar]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const htmlValue = event.target.value;
      if (!showTimeSelect && normalizedAllowedDates && htmlValue && !normalizedAllowedDates.has(htmlValue)) {
        setHasInvalidSelection(true);
        setInternalValue(formatForInput(value));
        return;
      }
      setInternalValue(htmlValue);
      setHasInvalidSelection(false);
      if (!htmlValue) { onChange?.(''); return; }
      const parsed = dayjs(htmlValue);
      if (parsed.isValid() && parsed.year() >= 1970) {
        if (showTimeSelect) {
          onChange?.(parsed.toISOString());
        } else {
          const [year, month, day] = htmlValue.split('-').map(Number);
          const utcDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
          onChange?.(utcDate.toISOString());
        }
      }
    },
    [formatForInput, normalizedAllowedDates, onChange, showTimeSelect, value]
  );

  const inputClasses = useMemo(() => {
    const base = 'w-full px-3 py-2 border rounded-md text-sm transition-colors focus:outline-hidden focus:ring-1';
    const state = error
      ? 'border-error-base focus:ring-error-base focus:border-error-base'
      : 'border-stroke-sub-300 focus:ring-primary focus:border-primary';
    const dis = disabled
      ? 'bg-bg-weak-50 cursor-not-allowed text-text-sub-600'
      : 'bg-bg-white-0 text-text-strong-950';
    const ph = !value ? 'text-text-soft-400' : '';
    return `${base} ${state} ${dis} ${ph}`.trim();
  }, [error, disabled, value]);

  const defaultPlaceholder = placeholder || (showTimeSelect ? 'Select date and time' : 'Select date');
  const displayedHelperText = hasInvalidSelection ? invalidDateMessage : helperText;
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const calendarDays = useMemo(() => {
    if (!useCustomCalendar) return [];
    const startOfCalendar = currentMonth.startOf('month').startOf('week');
    return Array.from({ length: 42 }, (_, index) => {
      const date = startOfCalendar.add(index, 'day');
      const formattedDate = date.format('YYYY-MM-DD');
      return {
        key: formattedDate,
        label: date.date(),
        value: formattedDate,
        isCurrentMonth: date.month() === currentMonth.month(),
        isSelected: internalValue === formattedDate,
        isAllowed: normalizedAllowedDates?.has(formattedDate) || false,
      };
    });
  }, [currentMonth, internalValue, normalizedAllowedDates, useCustomCalendar]);

  const selectedDisplayValue = useMemo(() => {
    if (!internalValue) return '';
    const parsed = dayjs(internalValue);
    return parsed.isValid() ? parsed.format('dddd, DD MMMM YYYY') : internalValue;
  }, [internalValue]);

  const canNavigatePrevious = useMemo(() => {
    if (!normalizedAllowedDates) return true;
    return Array.from(normalizedAllowedDates).some((date) =>
      dayjs(date).isBefore(currentMonth.startOf('month'), 'month')
    );
  }, [currentMonth, normalizedAllowedDates]);

  const canNavigateNext = useMemo(() => {
    if (!normalizedAllowedDates) return true;
    return Array.from(normalizedAllowedDates).some((date) =>
      dayjs(date).isAfter(currentMonth.endOf('month'), 'day')
    );
  }, [currentMonth, normalizedAllowedDates]);

  const handleCalendarSelect = (selectedDate: string) => {
    if (!normalizedAllowedDates?.has(selectedDate)) return;
    setInternalValue(selectedDate);
    setHasInvalidSelection(false);
    setIsCalendarOpen(false);
    const [year, month, day] = selectedDate.split('-').map(Number);
    const utcDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
    onChange?.(utcDate.toISOString());
  };

  return (
    <div className={className} ref={containerRef}>
      {useCustomCalendar ? (
        <div className="relative">
          <button
            type="button"
            disabled={disabled}
            onClick={() => !disabled && setIsCalendarOpen((prev) => !prev)}
            className={`${inputClasses} text-left flex items-center justify-between`}
            aria-describedby={displayedHelperText ? `${inputProps.id || 'datepicker'}-help` : undefined}
          >
            <span className={selectedDisplayValue ? 'text-text-strong-950' : 'text-text-soft-400'}>
              {selectedDisplayValue || defaultPlaceholder}
            </span>
            <i className="ri-calendar-line text-icon-soft-400 ml-2"></i>
          </button>

          {isCalendarOpen && !disabled && (
            <div className="absolute left-0 mt-2 w-[320px] rounded-lg border border-stroke-soft-200 bg-bg-white-0 shadow-card-md z-50 p-4">
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={() => canNavigatePrevious && setCurrentMonth((prev) => prev.subtract(1, 'month'))}
                  disabled={!canNavigatePrevious}
                  className="p-1 rounded-sm hover:bg-bg-weak-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-left-s-line text-icon-sub-600"></i>
                </button>
                <span className="text-sm font-medium text-text-strong-950">
                  {currentMonth.format('MMMM YYYY')}
                </span>
                <button
                  type="button"
                  onClick={() => canNavigateNext && setCurrentMonth((prev) => prev.add(1, 'month'))}
                  disabled={!canNavigateNext}
                  className="p-1 rounded-sm hover:bg-bg-weak-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-right-s-line text-icon-sub-600"></i>
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {dayLabels.map((day) => (
                  <div key={day} className="py-2 text-xs font-medium text-text-soft-400">{day}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day) => (
                  <button
                    key={day.key}
                    type="button"
                    onClick={() => day.isAllowed && handleCalendarSelect(day.value)}
                    disabled={!day.isAllowed}
                    className={`h-9 rounded-md text-sm transition-colors ${
                      day.isSelected
                        ? 'bg-primary text-static-white'
                        : day.isAllowed
                          ? 'bg-feature-lighter text-primary hover:bg-feature-light font-medium'
                          : day.isCurrentMonth
                            ? 'text-text-disabled-300 cursor-not-allowed'
                            : 'text-text-disabled-300 cursor-not-allowed opacity-50'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <input
          type={inputType}
          value={internalValue}
          onChange={handleChange}
          disabled={disabled}
          placeholder={defaultPlaceholder}
          min={minFormatted}
          max={maxFormatted}
          className={inputClasses}
          aria-invalid={error || hasInvalidSelection}
          aria-describedby={displayedHelperText ? `${inputProps.id || 'datepicker'}-help` : undefined}
          {...inputProps}
        />
      )}
      {displayedHelperText && (
        <div
          id={`${inputProps.id || 'datepicker'}-help`}
          className={`mt-1 text-xs ${error || hasInvalidSelection ? 'text-error-base' : 'text-text-sub-600'}`}
        >
          {displayedHelperText}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
